import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { TranslationState } from 'lib/interfaces'
import { markSearchSource } from 'lib/analytics/events'
import { dictionaryRouteFor } from 'lib/dictionary-routes'
import { exceedsQueryLimit, preprocessQuery } from 'lib/preprocessQuery'

export interface T2EFormData {
   sourceText: string
}

const useTranslationForm = (textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>) => {
   const navigate = useNavigate()
   // The form is on the homepage and on both dictionary entry pages, so it
   // can't read what's currently on screen out of one route's search params.
   const router = useRouter()
   const { register, handleSubmit, setValue, getValues, reset, control } = useForm<T2EFormData>()

   const [translationState, setTranslationState] = useState(TranslationState.None)
   // Whether the last submission was longer than we can translate, so the page
   // can say so. Deliberately not in the URL: on a reload `?q=` holds exactly
   // what was searched, so nothing was lost on that visit and there's nothing
   // to report.
   const [queryTruncated, setQueryTruncated] = useState(false)

   const onSubmit = handleSubmit(({ sourceText }: T2EFormData) => {
      if (!sourceText) return

      // The URL carries the query that is actually going to be translated, not
      // the raw paste: sending the untrimmed text would promise the whole thing
      // and silently deliver the first MAX_QUERY_LENGTH characters.
      const query = preprocessQuery(sourceText)
      setQueryTruncated(exceedsQueryLimit(sourceText))

      // A single word goes straight to its canonical dictionary URL; anything
      // else — a phrase, a sentence, an entry id — is a search. A word that
      // turns out to have no entry is sent back to /?q= by the route's loader.
      const destination = dictionaryRouteFor(query) ?? {
         to: '/',
         search: { q: query },
      }

      // Resubmitting the same query navigates to an identical URL, which the
      // router treats as a no-op (no re-render), so there's nothing that would
      // ever clear the loading state - skip it rather than spin forever.
      if (router.buildLocation(destination).href === router.state.location.href) {
         router.invalidate()
         return
      }

      setTranslationState(TranslationState.Loading)
      // Marks the navigation as a deliberate search, so the analytics can tell
      // it apart from arriving on a link or clicking a word in a result.
      markSearchSource('form')
      navigate(destination)
   })

   // To reset the form, we need to:
   // 1) Cancel the loading animation, if any
   // 2) Reset the textarea back to empty
   // 3) Reset the results and the URL.

   // A full reset is used when clicking the reset button
   // A partial (no URL change) is used when clicking the logo,
   // as that already results in a URL change of its own.
   const resetForm = () => {
      setTranslationState(TranslationState.None)
      setQueryTruncated(false)
      reset()
      textareaRef.current?.focus()
   }

   const resetFormAndResult = () => {
      resetForm()
      navigate({ to: '/', replace: true })
   }

   // Set focus on first load
   useEffect(() => {
      textareaRef.current?.focus()
   }, [textareaRef])

   return {
      onSubmit,
      resetForm,
      resetFormAndResult,
      control,
      register,
      translationState,
      queryTruncated,
      setValue,
      getValues,
      setTranslationState,
   }
}

export default useTranslationForm
